import { Component, OnInit, Inject, ElementRef, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { CustomValidator } from '../../shared/_validators/custom-validator';
import { AppSelectorService } from 'src/app/shared/services/app-selector/app-selector.service';
import { DatacontextService } from 'src/app/data/datacontext.service';
import { CommonNotificationService } from 'src/app/shared/services/common-notification/common-notification.service';
import { AmplitudeLoggerService } from 'src/app/shared/services/amplitude-logger/amplitude-logger.service';
import { AnalyticsEvents } from 'src/app/shared/constants/analytics-events';
import JSONFormatter from 'json-formatter-js';
import { AceEditorModule } from 'ng2-ace-editor';
import { ActionsCommonService } from '../actions-common.service';
import { UtilService } from 'src/app/shared/services/util/util.service';

@Component({
  selector: 'app-deep-config-dialog',
  templateUrl: './deep-config-dialog.component.html',
  styleUrls: ['./deep-config-dialog.component.scss']
})
export class DeepConfigDialogComponent implements OnInit, OnDestroy {
  dcForm: FormGroup;
  availableValueTypes = ['String', 'Number', 'Boolean', 'JSON'];
  btnPromise: any;
  JSONValue: any = '{}';
  os: any;
  constructor(
    private el: ElementRef,
    private dialogRef: MatDialogRef<DeepConfigDialogComponent>,
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private appSelectorService: AppSelectorService,
    private datacontextService: DatacontextService,
    private actionsCommonService: ActionsCommonService,
    private amplitudeLoggerService: AmplitudeLoggerService,
    private utilService: UtilService,
    private commonNotificationService: CommonNotificationService
  ) { }

  ngOnInit() {
    this.utilService.hideScrollOnTopMostContainer();
    this.os = this.appSelectorService.getSelectedApp().os;
    this.createDcForm();
    if (this.data && this.data.deepconfig_id) {
      this.fillDcForm(this.data);
      if (this.data.type === 'JSON') {
        this.JSONValue = JSON.stringify(this.data.details.value, null, '\t');
        /* this.jsonFormatter = new JSONFormatter(this.data.details.value);
        this.el.nativeElement.querySelector('#json-container').appendChild(this.jsonFormatter.render()); */
      }
    }

    if (this.data && this.data.module_id) {
      this.dcForm.get('module_id').patchValue(this.data.module_id);
    }
    if (this.data && this.data.module_name) {
      // this.dcForm.get('module_name').patchValue(this.data.module_name);
    }
  }

  ngOnDestroy() {
    this.utilService.removeHideScrollClassFromTopMostContainer();
  }

  /**
   * Fill Dc Form from the list page
   */
  fillDcForm(data) {
    this.dcForm.get('deepconfig_id').patchValue(data.deepconfig_id);
    this.dcForm.get('app_id').patchValue(data.app_id);
    this.dcForm.get('key').patchValue(data.key);
    this.dcForm.get('type').patchValue(data.type);
    this.dcForm.get('desc').patchValue(data.desc);
    this.dcForm.get('details.value').patchValue(data.details.value);
    this.dcForm.get('is_active').patchValue(data.is_active);
    this.dcForm.get('module_id').patchValue(data.module_id);
    if (data.type === 'Boolean') {
      this.dcForm.get('details.value').setValidators([]);
    } else if (data.type === 'Number') {
      this.dcForm.get('details.value').setValidators([
        Validators.required,
        CustomValidator.numberValidator
      ]);
    } else {
      this.dcForm.get('details.value').setValidators([]);
    }
  }

  /**
   * Create DC form with initial values and validations
   */
  createDcForm() {
    this.dcForm = this.formBuilder.group({
      deepconfig_id: [''],
      app_id: [''],
      key: ['', [Validators.required, CustomValidator.deepConfigNameValidator]],
      type: [null, Validators.required],
      desc: [''],
      module_id: [''],
      details: this.formBuilder.group({
        value: [null]
      }),
      is_active: [true]
    });
  }

  /**
   * Resets the value for passed data types
   *
   * @param   String  valueType
   */
  changeValueType(valueType) {
    if (valueType === 'Boolean') {
      this.dcForm.get('details.value').setValidators([]);
      this.dcForm.get('details.value').patchValue(false);
    } else if (valueType === 'Number') {
      this.dcForm.get('details.value').patchValue(0);
      this.dcForm.get('details.value').setValidators([
        Validators.required,
        CustomValidator.numberValidator
      ]);
    } else if (valueType === 'JSON') {
      this.JSONValue = JSON.stringify({}, null, '\t');
    } else {
      this.dcForm.get('details.value').setValidators([]);
      this.dcForm.get('details.value').patchValue('');
    }
  }

  /**
   * Submits the DC form to the server
   */
  onDcFormSubmit(val) {
    if (val.type === 'JSON') {
      try {
        val.details.value = JSON.parse(this.JSONValue);
        const jsonSize: any = this.actionsCommonService.memorySizeOf(this.JSONValue);
        if (jsonSize > this.actionsCommonService.JSONMaxSize) {
          this.commonNotificationService.showSnackBar('Ensure that JSON is less than 20 KB. Please contact us to use a larger JSON', 'error');
          return;
        }
      } catch (e) {
        this.commonNotificationService.showSnackBar('Invalid JSON', 'error');
        return;
      }
    }
    if (!val.deepconfig_id) {
      this.btnPromise = this.createDeepConfig(val);
    } else {
      this.btnPromise = this.updateDeepConfig(val);
    }
  }

  createDeepConfig(val) {
    if (!val.app_id) {
      val.app_id = this.appSelectorService.getSelectedApp().id;
    }
    return this.datacontextService.datasource.createDeepConfigByParams(
      val.key,
      val.type,
      val.app_id,
      val.details,
      val.desc,
      val.is_active,
      val.module_id
    ).subscribe((data: any) => {
      if (data.is_error) {
        return;
      }
      this.amplitudeLoggerService.log(
        AnalyticsEvents.eventName.CONFIG_CREATE,
        AnalyticsEvents.eventAction.CREATE_FINISH, {
          app_name: this.appSelectorService.getSelectedApp().name,
          name_config: val.key,
          value_type: val.type,
          value: val.details.value,
          name_config_set: this.data.module_name
        }
      );
      const dataToSend = val;
      val.deepconfig_id = data.api_response.deepconfig_id;
      this.dialogRef.close(dataToSend);

      let notificationMsg = '"' + val.key + '" successfully created';
      if (val.module_name) {
        notificationMsg += ' within Native Nudge "' + val.module_name + '"';
      }
      this.commonNotificationService.showSnackBar(notificationMsg, 'success');
    }, (err) => {
    });
  }

  updateDeepConfig(val) {
    return this.datacontextService.datasource.updateDeepConfig(
      val.deepconfig_id,
      val.key,
      val.type,
      val.app_id,
      val.details,
      val.desc,
      val.is_active,
      val.module_id
    ).subscribe((data: any) => {
      if (data.is_error) {
        return;
      }

      const notificationMsg = '"' + val.key + '" successfully updated';
      this.commonNotificationService.showSnackBar(notificationMsg, 'success');

      this.dialogRef.close(val);
    }, (err) => {
    });
  }

  closeDialog() {
    this.dialogRef.close();
  }
}
